/**
 * SubjectFormModal — create/edit a subject.
 *
 * Validation mirrors backend bounds in `features/catalog/schemas.py`:
 *   duration_minutes  15–360
 *   max_students      1–100
 *   price_per_session ≥ 0
 *   color             #rrggbb
 */

import React, { useEffect, useState } from 'react';
import toast from 'react-hot-toast';

import { Button, Input, Modal } from '@/components/common';
import {
  useCreateSubject,
  useUpdateSubject,
  type SubjectResponse,
} from '@features/catalog/api/subjects';
import { useCatalogConfig } from '@features/catalog/api/config';
import { paiseToRupeeInput, rupeeInputToPaise } from '@features/catalog/config/ui';
import { ApiError } from '@core/api/client';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  subject?: SubjectResponse;
}

const EMPTY = {
  name: '',
  category: '',
  duration_minutes: 30,
  max_students: 1,
  price_per_session: 0,
  color: '#6366f1',
};

export const SubjectFormModal: React.FC<Props> = ({ isOpen, onClose, subject }) => {
  const [form, setForm] = useState(EMPTY);
  // Price is entered in RUPEES (e.g. 29000.00) but stored/sent as paise.
  const [priceInput, setPriceInput] = useState('0');
  const createMut = useCreateSubject();
  const updateMut = useUpdateSubject();
  const { rules } = useCatalogConfig();
  const isEdit = Boolean(subject);

  // H8 — sanitise numeric inputs against server bounds before submit.
  const clampNumber = (n: number, lo: number, hi: number) =>
    Math.max(lo, Math.min(hi, Number.isFinite(n) ? n : lo));

  useEffect(() => {
    if (isOpen) {
      setForm(
        subject
          ? {
              name: subject.name,
              category: subject.category,
              duration_minutes: subject.duration_minutes,
              max_students: subject.max_students,
              price_per_session: subject.price_per_session,
              color: subject.color,
            }
          : EMPTY,
      );
      setPriceInput(subject ? paiseToRupeeInput(subject.price_per_session) : '0');
    }
  }, [isOpen, subject]);

  const submit = async () => {
    const payload = {
      ...form,
      duration_minutes: clampNumber(
        form.duration_minutes,
        rules.subject_duration_min_minutes,
        rules.subject_duration_max_minutes,
      ),
      max_students: clampNumber(
        form.max_students,
        rules.subject_max_students_min,
        rules.subject_max_students_max,
      ),
      price_per_session: Math.max(0, rupeeInputToPaise(priceInput)),
    };
    try {
      if (isEdit && subject) {
        await updateMut.mutateAsync({ id: subject.id, body: payload });
        toast.success('Subject updated');
      } else {
        await createMut.mutateAsync({ ...payload, status: 'active' });
        toast.success('Subject created');
      }
      onClose();
    } catch (err) {
      const msg = err instanceof ApiError ? err.message : 'Failed to save subject';
      toast.error(msg);
    }
  };

  const set = <K extends keyof typeof form>(key: K, value: (typeof form)[K]) =>
    setForm((s) => ({ ...s, [key]: value }));

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={isEdit ? 'Edit Subject' : 'New Subject'}
      size="md"
      data-testid="candeza-catalog-subject-modal"
    >
      <div style={{ display: 'grid', gap: 12, gridTemplateColumns: 'repeat(2, 1fr)' }}>
        <Input
          label="Name"
          value={form.name}
          onChange={(e) => set('name', e.target.value)}
          data-testid="candeza-catalog-subject-name-input"
        />
        <Input
          label="Category"
          value={form.category}
          onChange={(e) => set('category', e.target.value)}
          placeholder="Instrument, Vocal, Dance…"
          data-testid="candeza-catalog-subject-category-input"
        />
        <Input
          label="Duration (minutes)"
          type="number"
          min={rules.subject_duration_min_minutes}
          max={rules.subject_duration_max_minutes}
          step={5}
          value={String(form.duration_minutes)}
          onChange={(e) => set('duration_minutes', Number(e.target.value))}
          data-testid="candeza-catalog-subject-duration-input"
        />
        <Input
          label="Max students"
          type="number"
          min={rules.subject_max_students_min}
          max={rules.subject_max_students_max}
          step={1}
          value={String(form.max_students)}
          onChange={(e) => set('max_students', Number(e.target.value))}
          data-testid="candeza-catalog-subject-maxstudents-input"
        />
        <Input
          label="Price per session (₹)"
          type="number"
          min={0}
          step="0.01"
          value={priceInput}
          onChange={(e) => setPriceInput(e.target.value)}
          placeholder="e.g. 500.00"
          data-testid="candeza-catalog-subject-price-input"
        />
        <Input
          label="Colour"
          value={form.color}
          onChange={(e) => set('color', e.target.value)}
          placeholder="#6366f1"
          data-testid="candeza-catalog-subject-color-input"
        />
      </div>
      <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 24 }}>
        <Button variant="outline" onClick={onClose}>Cancel</Button>
        <Button
          onClick={submit}
          isLoading={createMut.isPending || updateMut.isPending}
          data-testid="candeza-catalog-subject-submit-btn"
        >
          {isEdit ? 'Save changes' : 'Create subject'}
        </Button>
      </div>
    </Modal>
  );
};

